import { useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "../../../contexts/permissionsContext.js";
import { useMobileDrawerContext } from "../../../contexts/mobile-drawer-context.js";
import useIsMobile from "../../../hooks/useIsMobile.js";
import { MOBILE_TABBAR_TABS_CONFIG } from "../constants/mobile-tabbar-tabs-config.js";
import { MOBILE_TABBAR_UI_CONFIG } from "../constants/mobile-tabbar-ui-config.js";
import { filterVisibleMobileTabs } from "../lib/filter-visible-mobile-tabs.js";
import { getActiveMobileTab } from "../lib/get-active-mobile-tab.js";

/**
 * @param {"solid"|"floating"} [variant]
 */
export function useMobileTabbar(variant = "floating") {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { permissions: userRoles } = usePermissions();
  const isMobile = useIsMobile();
  const { isDrawerOpen } = useMobileDrawerContext() || {};

  const tabs = useMemo(
    () =>
      filterVisibleMobileTabs(
        MOBILE_TABBAR_TABS_CONFIG,
        userRoles,
        MOBILE_TABBAR_UI_CONFIG.maxVisibleTabs,
      ),
    [userRoles],
  );

  const activeTab = useMemo(
    () => getActiveMobileTab(pathname, tabs),
    [pathname, tabs],
  );

  const onTabPress = useCallback(
    (tab) => {
      if (tab?.path && pathname !== tab.path) {
        navigate(tab.path);
      }
    },
    [navigate, pathname],
  );

  return {
    visible: isMobile && tabs.length > 0,
    tabs,
    activeKey: activeTab?.key ?? "",
    variant,
    isDrawerOpen: Boolean(isDrawerOpen),
    onTabPress,
  };
}
